import type { MarketingPost, MarketingResponse } from "@/lib/api";
import { cn } from "@/lib/formatters";

const CAP = 2;

interface OverviewProps {
  posts: MarketingPost[];
  week: MarketingResponse["week"];
  summary: MarketingResponse["summary"];
  pendientes: number;
}

/**
 * Lo que hay que saber antes de scrollear.
 *
 * La página tenía toda la información y ninguna jerarquía: había que recorrer
 * cada post para descubrir si algo pedía atención. Acá van sólo las cifras que
 * cambian lo que uno hace a continuación, y las dos primeras son accionables:
 * un triage esperando y un entregable sin producir son trabajo pendiente; el
 * resto es contexto.
 */
export function Overview({ posts, week, summary, pendientes }: OverviewProps) {
  const aProducir = posts.reduce(
    (n, p) => n + p.platforms.filter(
      (x) => x.decision === "publicar" && x.needs_production).length,
    0,
  );
  const ocupados = week.slots.reduce((n, s) => n + s.occupants.length, 0);
  const capacidad = week.slots.length * CAP;

  const cifras = [
    {
      valor: pendientes,
      label: pendientes === 1 ? "esperando" : "esperando",
      detalle: "triage sin aprobar",
      urgente: pendientes > 0,
    },
    {
      valor: aProducir,
      label: "a producir",
      detalle: "carruseles y guiones sin renderizar",
      urgente: aProducir > 0,
    },
    {
      valor: `${capacidad - ocupados}`,
      label: "slots libres",
      detalle: `de ${capacidad} esta semana`,
      urgente: false,
    },
    {
      valor: summary.decisions.publicar ?? 0,
      label: "publican",
      detalle: `sobre ${summary.posts} posts`,
      urgente: false,
    },
    {
      valor: `$${summary.estimated_usd}`,
      label: "estimado",
      detalle: "referencia con precio de Opus 5, no una factura",
      urgente: false,
    },
  ];

  return (
    <div className="surface p-4">
      <div className="flex flex-wrap gap-x-8 gap-y-4">
        {cifras.map((c) => (
          <div key={c.label} title={c.detalle} className="min-w-[92px]">
            <div
              className={cn(
                "font-mono text-2xl tabular-nums leading-none",
                c.urgente ? "text-primary" : "text-text",
              )}
            >
              {c.valor}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-muted mt-1.5">
              {c.label}
            </div>
          </div>
        ))}

        {/* La ocupación como barra: cuántos casilleros quedan se lee de un
            vistazo mucho mejor que "9/14". */}
        <div className="flex-1 min-w-[180px] flex flex-col justify-center gap-2">
          <div className="flex items-baseline justify-between font-mono text-[10px] text-muted">
            <span>ocupación · {week.week ?? "—"}</span>
            <span className="tabular-nums">{ocupados}/{capacidad}</span>
          </div>
          <div className="flex gap-1" aria-hidden>
            {week.slots.map((s) =>
              Array.from({ length: CAP }).map((_, i) => (
                <div
                  key={`${s.platform}-${i}`}
                  title={
                    s.occupants[i]
                      ? `${s.platform}: ${s.occupants[i].slug}`
                      : `${s.platform}: libre`
                  }
                  className={cn(
                    "h-6 flex-1 rounded-sm border",
                    s.occupants[i]
                      ? "border-primary/50 bg-primary/20"
                      : "border-dashed border-border",
                  )}
                />
              )),
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
